/**
 * Typed fetch wrapper for the VAM API.
 * - Base URL from VITE_API_BASE_URL (default `/api/v1`).
 * - Errors are RFC 9457 problem+json and surface as `ApiError`.
 * - Cursor pagination helpers for `{ items, next_cursor }` lists.
 * - Responses are parsed with zod (see `schemas.ts`) so a shape change is a
 *   loud, localised failure instead of a blank page.
 * - In Vite dev builds only, `VITE_DEV_USER` sends the X-Vam-Dev-User header
 *   so the console can talk to a dev API running with VAM_DEV_AUTH=true
 *   without signing in.
 */
import type { z } from 'zod';
import * as S from './schemas.js';

export const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '/api/v1').replace(/\/$/, '');

export interface Problem {
  type: string;
  title: string;
  status: number;
  detail?: string;
  instance?: string;
  errors?: { path: string; message: string; code?: string }[];
  [key: string]: unknown;
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly problem: Problem,
  ) {
    super(problem.detail ?? problem.title);
    this.name = 'ApiError';
  }

  /** Field-level messages for validation problems, keyed by path. */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const e of this.problem.errors ?? []) out[e.path] ??= e.message;
    return out;
  }

  /** The module named by a 501 stub response, when the API says so. */
  get notImplementedFeature(): string | null {
    if (this.status !== 501) return null;
    const feature = this.problem.feature;
    return typeof feature === 'string' ? feature : 'this module';
  }
}

/** Thrown when the API answered but not in the shape the console understands. */
export class ResponseShapeError extends Error {
  constructor(
    public readonly path: string,
    public readonly issues: string[],
  ) {
    super(`unexpected response shape from ${path}: ${issues.join('; ')}`);
    this.name = 'ResponseShapeError';
  }
}

export interface Page<T> {
  items: T[];
  next_cursor: string | null;
}

export type Query = Record<string, string | number | boolean | undefined | null>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Query;
  signal?: AbortSignal;
  headers?: Record<string, string>;
}

export function buildUrl(path: string, query?: Query): string {
  const url = path.startsWith('http')
    ? path
    : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

export function devAuthHeaders(): Record<string, string> {
  const user = import.meta.env.VITE_DEV_USER;
  return import.meta.env.DEV && user ? { 'X-Vam-Dev-User': user } : {};
}

// --- Unauthorised handling ---------------------------------------------------

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/** Register a redirect-to-login handler; returns an unsubscribe function. */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

function notifyUnauthorized(): void {
  for (const listener of unauthorizedListeners) listener();
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json, application/problem+json',
    ...devAuthHeaders(),
    ...options.headers,
  };
  let body: string | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }
  const response = await fetch(buildUrl(path, options.query), {
    method: options.method ?? 'GET',
    headers,
    body,
    credentials: 'same-origin',
    signal: options.signal,
  });
  if (!response.ok) {
    const problem = await readProblem(response);
    if (response.status === 401) notifyUnauthorized();
    throw new ApiError(response.status, problem);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function readProblem(response: Response): Promise<Problem> {
  const fallback: Problem = {
    type: 'about:blank',
    title: response.statusText || 'Request failed',
    status: response.status,
  };
  try {
    const text = await response.text();
    if (!text) return fallback;
    const parsed = JSON.parse(text) as Partial<Problem>;
    return {
      ...fallback,
      ...parsed,
      status: parsed.status ?? response.status,
      title: parsed.title ?? fallback.title,
    };
  } catch {
    return fallback;
  }
}

/** Fetch one page of a cursor-paginated list. */
export function fetchPage<T>(
  path: string,
  query: Query & { limit?: number; cursor?: string | null } = {},
): Promise<Page<T>> {
  return api<Page<T>>(path, { query: { ...query, cursor: query.cursor ?? undefined } });
}

/** Follow `next_cursor` until exhausted (bounded by maxPages). */
export async function fetchAllPages<T>(
  path: string,
  query: Query = {},
  maxPages = 20,
): Promise<T[]> {
  const items: T[] = [];
  let cursor: string | null = null;
  for (let i = 0; i < maxPages; i++) {
    const page: Page<T> = await fetchPage<T>(path, { ...query, cursor });
    items.push(...page.items);
    cursor = page.next_cursor;
    if (!cursor) break;
  }
  return items;
}

// --- Parsed helpers ----------------------------------------------------------

/** Parse a payload with a zod schema, raising a readable error on mismatch. */
export function parseWith<T extends z.ZodTypeAny>(
  schema: T,
  path: string,
  data: unknown,
): z.output<T> {
  const result = schema.safeParse(data);
  if (result.success) return result.data;
  throw new ResponseShapeError(
    path,
    result.error.issues.slice(0, 4).map((i) => `${i.path.join('.') || '<root>'}: ${i.message}`),
  );
}

async function get<T extends z.ZodTypeAny>(
  schema: T,
  path: string,
  query?: Query,
): Promise<z.output<T>> {
  return parseWith(schema, path, await api<unknown>(path, { query }));
}

async function send<T extends z.ZodTypeAny>(
  schema: T,
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  query?: Query,
): Promise<z.output<T>> {
  const payload = await api<unknown>(path, { method, body, query });
  return parseWith(schema, path, payload);
}

async function listOf<T extends z.ZodTypeAny>(
  item: T,
  path: string,
  query: Query = {},
): Promise<{ items: z.output<T>[]; next_cursor: string | null }> {
  const raw = await api<unknown>(path, { query });
  return parseWith(S.pageOf(item), path, raw);
}

/** Follow every page of a parsed list (used for pickers and small lookups). */
async function listAllOf<T extends z.ZodTypeAny>(
  item: T,
  path: string,
  query: Query = {},
  maxPages = 10,
): Promise<z.output<T>[]> {
  const out: z.output<T>[] = [];
  let cursor: string | null = null;
  for (let i = 0; i < maxPages; i++) {
    const page: { items: z.output<T>[]; next_cursor: string | null } = await listOf(item, path, {
      ...query,
      limit: 200,
      cursor: cursor ?? undefined,
    });
    out.push(...page.items);
    cursor = page.next_cursor;
    if (!cursor) break;
  }
  return out;
}

// --- Resources ---------------------------------------------------------------

export interface Team {
  id: string;
  name: string;
  slug: string;
  member_count: number;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export const teamsApi = {
  list: (query: { limit?: number; cursor?: string | null; q?: string } = {}) =>
    fetchPage<Team>('/teams', query),
  get: (id: string) => api<Team>(`/teams/${id}`),
  create: (input: { name: string; slug?: string }) =>
    api<Team>('/teams', { method: 'POST', body: input }),
  update: (id: string, input: { name?: string; slug?: string }) =>
    api<Team>(`/teams/${id}`, { method: 'PATCH', body: input }),
  remove: (id: string) => api<void>(`/teams/${id}`, { method: 'DELETE' }),
  listAll: () => listAllOf(S.TeamSchema, '/teams'),
  /**
   * The directory's copy, which labels alarms and timeline rows and so has to
   * keep resolving the name of a soft-deleted team. The API ignores
   * `include_deleted` for team-scoped roles, so this is never wider than the
   * caller's own access. Pickers use `listAll`, which lists live teams only.
   */
  listAllWithDeleted: () => listAllOf(S.TeamSchema, '/teams', { include_deleted: true }),
};

export interface Health {
  status: 'ok' | 'degraded';
  version: string;
  db: { ok: boolean; latency_ms: number | null };
  redis: { ok: boolean; latency_ms: number | null };
}

export const healthApi = {
  get: () => api<Health>('/health'),
};

export const authApi = {
  me: () => get(S.MeSchema, '/auth/me'),
  localLogin: (input: { email: string; password: string }) =>
    send(S.MeSchema, 'POST', '/auth/local/login', input),
  logout: () => api<void>('/auth/logout', { method: 'POST' }),
  revokeAllSessions: () => api<void>('/auth/sessions', { method: 'DELETE' }),
  /** Full-page navigation; Entra answers with a 302 the SPA cannot follow. */
  entraLoginUrl: (returnTo?: string) =>
    buildUrl('/auth/login', returnTo ? { return_to: returnTo } : undefined),
};

export interface AlarmListQuery {
  limit?: number;
  cursor?: string | null;
  /** `open`, `closed`, … or several comma separated; `active` expands. */
  state?: string;
  severity?: string;
  integration_id?: string;
  team_id?: string;
  assignee_id?: string;
  tag?: string;
  entity?: string;
  q?: string;
  /** `last_seen:desc` style; split into the API's `sort` + `order`. */
  sort?: string;
  since?: string;
  until?: string;
  suppressed?: boolean;
  flapping?: boolean;
  group_by?: string;
  group_limit?: number;
  /** URL-encoded condition DSL JSON (docs/06 section 4). */
  filter?: string;
}

/** The three states an alarm can be in while it still needs attention. */
export const ACTIVE_STATES = 'open,acknowledged,snoozed';

/**
 * The console's filter model is a superset of the wire format: it carries a
 * pseudo-state `active` and a combined `field:direction` sort. Translate here,
 * once, so no page has to remember the difference.
 */
export function toAlarmQuery(query: AlarmListQuery): Query {
  const { state, sort, ...rest } = query;
  const wire: Query = { ...(rest as Query) };
  if (state) wire.state = state === 'active' ? ACTIVE_STATES : state;
  if (sort) {
    const [field, direction] = sort.split(':');
    if (field) wire.sort = field;
    if (direction) wire.order = direction;
  }
  return wire;
}

export interface SnoozeInput {
  minutes?: number;
  until?: string;
  note?: string;
}

export const alarmsApi = {
  list: (query: AlarmListQuery = {}) =>
    api<unknown>('/alarms', { query: toAlarmQuery(query) }).then((raw) =>
      parseWith(S.AlarmPageSchema, '/alarms', raw),
    ),
  get: (id: string) => get(S.AlarmSchema, `/alarms/${id}`),
  timeline: (id: string, query: { limit?: number; cursor?: string | null; kind?: string } = {}) =>
    listOf(S.TimelineItemSchema, `/alarms/${id}/timeline`, query as Query),
  events: (id: string, query: { limit?: number; cursor?: string | null } = {}) =>
    listOf(S.AlarmEventSchema, `/alarms/${id}/events`, query as Query),
  ack: (id: string, note?: string) =>
    send(S.AlarmSchema, 'POST', `/alarms/${id}/ack`, note ? { note } : {}),
  unack: (id: string) => send(S.AlarmSchema, 'POST', `/alarms/${id}/unack`, {}),
  close: (id: string, reason?: string) =>
    send(S.AlarmSchema, 'POST', `/alarms/${id}/close`, reason ? { reason } : {}),
  reopen: (id: string) => send(S.AlarmSchema, 'POST', `/alarms/${id}/reopen`, {}),
  snooze: (id: string, input: SnoozeInput) =>
    send(S.AlarmSchema, 'POST', `/alarms/${id}/snooze`, {
      until: input.until ?? new Date(Date.now() + (input.minutes ?? 60) * 60_000).toISOString(),
      ...(input.note ? { note: input.note } : {}),
    }),
  unsnooze: (id: string) => send(S.AlarmSchema, 'POST', `/alarms/${id}/unsnooze`, {}),
  assign: (id: string, assigneeId: string | null) =>
    send(S.AlarmSchema, 'POST', `/alarms/${id}/assign`, { assignee_id: assigneeId }),
  setSeverity: (id: string, severity: string) =>
    send(S.AlarmSchema, 'POST', `/alarms/${id}/severity`, { severity }),
  setTeam: (id: string, teamId: string) =>
    send(S.AlarmSchema, 'POST', `/alarms/${id}/team`, { team_id: teamId }),
  setTags: (id: string, add: string[], remove: string[]) =>
    send(S.AlarmSchema, 'POST', `/alarms/${id}/tags`, { add, remove }),
  comment: (id: string, body: string, notify: boolean) =>
    api<unknown>(`/alarms/${id}/comment`, { method: 'POST', body: { body, notify } }),
  share: (id: string, input: { outbound_integration_id: string }) =>
    api<unknown>(`/alarms/${id}/share`, { method: 'POST', body: input }),
  ticket: (id: string, input: { outbound_integration_id: string }) =>
    api<unknown>(`/alarms/${id}/ticket`, { method: 'POST', body: input }),
  remove: (id: string) => api<void>(`/alarms/${id}`, { method: 'DELETE' }),
  /** `POST /alarms/bulk/:action`; the payload carries the action's arguments. */
  bulk: (action: string, ids: string[], payload: Record<string, unknown> = {}) =>
    send(S.BulkResponseSchema, 'POST', `/alarms/bulk/${action}`, { ids, ...payload }),
};

export const viewsApi = {
  list: () => listOf(S.SavedViewSchema, '/views', { limit: 200 }),
  mine: () => listOf(S.SavedViewSchema, '/views/mine', { limit: 200 }),
  create: (input: Record<string, unknown>) => send(S.SavedViewSchema, 'POST', '/views', input),
  update: (id: string, input: Record<string, unknown>) =>
    send(S.SavedViewSchema, 'PATCH', `/views/${id}`, input),
  remove: (id: string) => api<void>(`/views/${id}`, { method: 'DELETE' }),
};

export const integrationsApi = {
  list: (query: Query = {}) => listOf(S.IntegrationSchema, '/integrations', query),
  listAll: () => listAllOf(S.IntegrationSchema, '/integrations'),
  get: (id: string) => get(S.IntegrationSchema, `/integrations/${id}`),
  create: (input: Record<string, unknown>) =>
    send(S.IntegrationSchema, 'POST', '/integrations', input),
  update: (id: string, input: Record<string, unknown>) =>
    send(S.IntegrationSchema, 'PATCH', `/integrations/${id}`, input),
  remove: (id: string) => api<void>(`/integrations/${id}`, { method: 'DELETE' }),
  test: (id: string) => send(S.IntegrationTestResultSchema, 'POST', `/integrations/${id}/test`, {}),
  setup: (id: string) => get(S.SetupInstructionsSchema, `/integrations/${id}/setup`),
  rotateApiKey: (id: string) =>
    send(S.RotatedApiKeySchema, 'POST', `/integrations/${id}/rotate-api-key`, {}),
  rotateUrlToken: (id: string) =>
    send(S.RotatedUrlTokenSchema, 'POST', `/integrations/${id}/rotate-url-token`, {}),
};

export const outboundApi = {
  list: (query: Query = {}) => listOf(S.OutboundSchema, '/outbound-integrations', query),
  listAll: () => listAllOf(S.OutboundSchema, '/outbound-integrations'),
  get: (id: string) => get(S.OutboundSchema, `/outbound-integrations/${id}`),
  create: (input: Record<string, unknown>) =>
    send(S.OutboundSchema, 'POST', '/outbound-integrations', input),
  update: (id: string, input: Record<string, unknown>) =>
    send(S.OutboundSchema, 'PATCH', `/outbound-integrations/${id}`, input),
  remove: (id: string) => api<void>(`/outbound-integrations/${id}`, { method: 'DELETE' }),
  test: (id: string, input: { to?: string } = {}) =>
    send(S.OutboundTestResultSchema, 'POST', `/outbound-integrations/${id}/test`, input),
};

export const contactsApi = {
  list: (query: Query = {}) => listOf(S.ContactSchema, '/contacts', query),
  create: (input: Record<string, unknown>) => send(S.ContactSchema, 'POST', '/contacts', input),
  update: (id: string, input: Record<string, unknown>) =>
    send(S.ContactSchema, 'PATCH', `/contacts/${id}`, input),
  remove: (id: string) => api<void>(`/contacts/${id}`, { method: 'DELETE' }),
};

export const usersApi = {
  list: (query: Query = {}) => listOf(S.UserSchema, '/users', query),
  listAll: () => listAllOf(S.UserSchema, '/users'),
  get: (id: string) => get(S.UserSchema, `/users/${id}`),
  me: () => get(S.UserSchema, '/users/me'),
  create: (input: Record<string, unknown>) => send(S.UserSchema, 'POST', '/users', input),
  update: (id: string, input: Record<string, unknown>) =>
    send(S.UserSchema, 'PATCH', `/users/${id}`, input),
  deactivate: (id: string) => api<void>(`/users/${id}`, { method: 'DELETE' }),
  addTeam: (id: string, teamId: string) =>
    send(S.UserSchema, 'POST', `/users/${id}/teams`, { team_id: teamId }),
  removeTeam: (id: string, teamId: string) =>
    api<void>(`/users/${id}/teams/${teamId}`, { method: 'DELETE' }),
};

export const apiKeysApi = {
  list: (query: Query = {}) => listOf(S.ApiKeySchema, '/api-keys', query),
  create: (input: Record<string, unknown>) =>
    send(S.CreatedApiKeySchema, 'POST', '/api-keys', input),
  revoke: (id: string) => api<void>(`/api-keys/${id}`, { method: 'DELETE' }),
};

export const rulesApi = {
  list: (query: Query = {}) => listOf(S.ProcessingRuleSchema, '/processing-rules', query),
  get: (id: string) => get(S.ProcessingRuleSchema, `/processing-rules/${id}`),
  create: (input: Record<string, unknown>) =>
    send(S.ProcessingRuleSchema, 'POST', '/processing-rules', input),
  update: (id: string, input: Record<string, unknown>) =>
    send(S.ProcessingRuleSchema, 'PATCH', `/processing-rules/${id}`, input),
  remove: (id: string) => api<void>(`/processing-rules/${id}`, { method: 'DELETE' }),
  reorder: (ids: string[]) =>
    api<unknown>('/processing-rules/reorder', { method: 'POST', body: { ids } }),
  dryRun: (input: Record<string, unknown>) =>
    send(S.RuleDryRunSchema, 'POST', '/processing-rules/dry-run', input),
  dryRunOne: (id: string, input: Record<string, unknown>) =>
    send(S.RuleDryRunSchema, 'POST', `/processing-rules/${id}/dry-run`, input),
};

export const policiesApi = {
  list: (query: Query = {}) => listOf(S.NotificationPolicySchema, '/notification-policies', query),
  get: (id: string) => get(S.NotificationPolicySchema, `/notification-policies/${id}`),
  create: (input: Record<string, unknown>) =>
    send(S.NotificationPolicySchema, 'POST', '/notification-policies', input),
  update: (id: string, input: Record<string, unknown>) =>
    send(S.NotificationPolicySchema, 'PATCH', `/notification-policies/${id}`, input),
  remove: (id: string) => api<void>(`/notification-policies/${id}`, { method: 'DELETE' }),
  /** Storms the notification worker is holding right now (storm guard). */
  storms: async () => (await get(S.ActiveStormListSchema, '/notification-policies/storms')).items,
};

export const escalationsApi = {
  list: (query: Query = {}) => listOf(S.EscalationPolicySchema, '/escalation-policies', query),
  listAll: () => listAllOf(S.EscalationPolicySchema, '/escalation-policies'),
  get: (id: string) => get(S.EscalationPolicySchema, `/escalation-policies/${id}`),
  create: (input: Record<string, unknown>) =>
    send(S.EscalationPolicySchema, 'POST', '/escalation-policies', input),
  update: (id: string, input: Record<string, unknown>) =>
    send(S.EscalationPolicySchema, 'PATCH', `/escalation-policies/${id}`, input),
  remove: (id: string) => api<void>(`/escalation-policies/${id}`, { method: 'DELETE' }),
};

export const schedulesApi = {
  list: (query: Query = {}) => listOf(S.ScheduleSchema, '/schedules', query),
  listAll: () => listAllOf(S.ScheduleSchema, '/schedules'),
  get: (id: string) => get(S.ScheduleSchema, `/schedules/${id}`),
  create: (input: Record<string, unknown>) => send(S.ScheduleSchema, 'POST', '/schedules', input),
  update: (id: string, input: Record<string, unknown>) =>
    send(S.ScheduleSchema, 'PATCH', `/schedules/${id}`, input),
  remove: (id: string) => api<void>(`/schedules/${id}`, { method: 'DELETE' }),
  addShift: (id: string, input: Record<string, unknown>) =>
    send(S.ShiftSchema, 'POST', `/schedules/${id}/shifts`, input),
  onCall: (id: string, query: { at?: string } = {}) =>
    get(S.OnCallResponseSchema, `/schedules/${id}/on-call`, query as Query),
  calendar: (id: string, query: { from: string; to: string }) =>
    get(S.CalendarResponseSchema, `/schedules/${id}/calendar`, query as Query),
  icsUrl: (id: string) => buildUrl(`/schedules/${id}/ics`),
};

export const overridesApi = {
  list: (query: Query = {}) => listOf(S.OverrideSchema, '/overrides', query),
  create: (input: Record<string, unknown>) => send(S.OverrideSchema, 'POST', '/overrides', input),
  remove: (id: string) => api<void>(`/overrides/${id}`, { method: 'DELETE' }),
};

export const maintenanceApi = {
  list: (query: Query = {}) => listOf(S.MaintenanceWindowSchema, '/maintenance-windows', query),
  /** `/active` answers with a bare array, not a page. */
  active: () =>
    api<unknown>('/maintenance-windows/active').then((raw) =>
      parseWith(S.MaintenanceWindowSchema.array(), '/maintenance-windows/active', raw),
    ),
  occurrences: (id: string, query: Query = {}) =>
    api<unknown>(`/maintenance-windows/${id}/occurrences`, { query }),
  get: (id: string) => get(S.MaintenanceWindowSchema, `/maintenance-windows/${id}`),
  create: (input: Record<string, unknown>) =>
    send(S.MaintenanceWindowSchema, 'POST', '/maintenance-windows', input),
  update: (id: string, input: Record<string, unknown>) =>
    send(S.MaintenanceWindowSchema, 'PATCH', `/maintenance-windows/${id}`, input),
  remove: (id: string) => api<void>(`/maintenance-windows/${id}`, { method: 'DELETE' }),
};

export interface ReportQuery extends Query {
  from?: string;
  to?: string;
  team_id?: string;
  integration_id?: string;
  limit?: number;
}

export const reportsApi = {
  summary: (query: ReportQuery = {}) => get(S.ReportSummarySchema, '/reports/summary', query),
  mtta: (query: ReportQuery = {}) => get(S.ReportDurationSchema, '/reports/mtta', query),
  mttr: (query: ReportQuery = {}) => get(S.ReportDurationSchema, '/reports/mttr', query),
  noisiest: (query: ReportQuery & { by?: string } = {}) =>
    get(S.ReportNoisiestSchema, '/reports/noisiest', query),
  /** A full-page navigation: the browser downloads the file itself. */
  exportUrl: (query: ReportQuery & { state?: string; severity?: string } = {}) =>
    buildUrl('/reports/export.csv', query),
};

export const settingsApi = {
  get: () => get(S.SettingsSchema, '/settings'),
  update: (input: Record<string, unknown>) => send(S.SettingsSchema, 'PATCH', '/settings', input),
};

export const pushApi = {
  config: () => get(S.PushConfigSchema, '/users/me/push-config'),
  /** Every browser this user has registered; an alarm reaches all of them. */
  list: () => get(S.PushSubscriptionListSchema, '/users/me/push-subscriptions'),
  subscribe: (subscription: unknown) =>
    api<unknown>('/users/me/push-subscriptions', { method: 'POST', body: subscription }),
  unsubscribe: (endpoint: string) =>
    api<void>('/users/me/push-subscriptions', { method: 'DELETE', body: { endpoint } }),
};
